/**
 * useActivityCaptureSettings — reads the tenant's per-source activity capture
 * policy from /api/tenant/activity-capture-settings.
 *
 * Each source can be:
 *   - 'off'      : not captured, not rendered in rep or manager UI
 *   - 'advisory' : captured + shown to rep in DailyReconcileBoxV2; no manager
 *                  surfacing, no required offline-note
 *   - 'enforced' : captured + shown to rep; manager Activity audit view is
 *                  populated; offline-note required when there is a positive
 *                  delta on any row
 *
 * Returns { settings, loading, error, refetch }. settings is null until the
 * first successful fetch. Failure mode is "treat as advisory" so reps never
 * see required-note prompts they don't understand.
 */

import { useCallback, useEffect, useState } from "react";

export type CaptureMode = "off" | "advisory" | "enforced";

export interface ActivityCaptureSettings {
  outlook_email: CaptureMode;
  outlook_calendar: CaptureMode;
  teams_phone: CaptureMode;
  linkedin_extension: CaptureMode;
  dialer_third_party: CaptureMode;
}

const DEFAULT_SETTINGS: ActivityCaptureSettings = {
  outlook_email: "off",
  outlook_calendar: "off",
  teams_phone: "off",
  linkedin_extension: "off",
  dialer_third_party: "off",
};

export function useActivityCaptureSettings(): {
  settings: ActivityCaptureSettings | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [settings, setSettings] = useState<ActivityCaptureSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/tenant/activity-capture-settings", {
        credentials: "include",
      });
      if (!res.ok) {
        // Soft-fail to defaults; this is a non-critical capability.
        setSettings(DEFAULT_SETTINGS);
        setError(`HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      setSettings({ ...DEFAULT_SETTINGS, ...(data?.settings ?? {}) });
    } catch (e: any) {
      setSettings(DEFAULT_SETTINGS);
      setError(e?.message ?? "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  return { settings, loading, error, refetch: fetchSettings };
}
